/**
 * WeekCalendar — a Mon–Sun strip with per-day event dots and a day modal
 * for adding / editing / sharing notes, meetings and private items.
 *
 * Rendered at the top of pages that need an at-a-glance agenda — Email,
 * Pontaj, etc. All events live in `doserne_calendar_events` on the backend;
 * sharing an event just adds a recipient email, who then sees it in their
 * own week view.
 *
 * Props:
 *   authHeader() → { headers: {...} }
 *   compact      → when true, uses tighter spacing (default false)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Users as UsersIcon, Clock, Lock, StickyNote, CalendarCheck, Loader2, Check, Bell, CalendarDays, Zap } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALE_FOR = { ro: "ro-RO", en: "en-GB", no: "nb-NO" };
const COLOR_DOT = { cyan: "bg-cyan-500", amber: "bg-amber-500", rose: "bg-rose-500", emerald: "bg-emerald-500", violet: "bg-violet-500", slate: "bg-slate-500" };

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // 0=Mon .. 6=Sun
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function iso(d) {
  // Format date in LOCAL time, not UTC — otherwise users east of UTC (CET/CEST)
  // whose local midnight is 23:00 UTC would see every click land on the previous
  // day. Bug reported Nov 4 2026.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isSameDay(a, b) { return a.toDateString() === b.toDateString(); }
// ISO week number (Mon-based) — matches Norway / most of Europe.
function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

export default function WeekCalendar({ authHeader, compact = false }) {
  const { t, lang } = useLang();
  const locale = LOCALE_FOR[lang] || "en-GB";
  // Day labels built from the current locale → auto-translate RO/EN/NO.
  const DAYS_LONG = useMemo(() => {
    const base = new Date(2024, 0, 1); // a Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base); d.setDate(base.getDate() + i);
      return d.toLocaleDateString(locale, { weekday: "long" });
    });
  }, [locale]);
  const DAYS_SHORT = useMemo(() => DAYS_LONG.map((s) => s.slice(0, 2)), [DAYS_LONG]);
  const KIND_META = useMemo(() => ({
    note: { label: t("cal.kind_note"), icon: StickyNote, cls: "bg-cyan-100 text-cyan-700" },
    meeting: { label: t("cal.kind_meeting"), icon: CalendarCheck, cls: "bg-emerald-100 text-emerald-700" },
    private: { label: t("cal.kind_private"), icon: Lock, cls: "bg-slate-200 text-slate-700" },
    share: { label: t("cal.kind_share"), icon: UsersIcon, cls: "bg-violet-100 text-violet-700" },
  }), [t]);

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dayCtx, setDayCtx] = useState(null); // selected day for modal
  const [showJump, setShowJump] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/calendar/week`, {
        ...authHeader(), params: { start: iso(weekStart) },
      });
      setEvents(data?.events || []);
    } catch (e) {
      // Silent fail — the UI shows empty state anyway.
    } finally { setLoading(false); }
  }, [weekStart, authHeader]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const grouped = useMemo(() => {
    const g = {};
    for (const e of events) { (g[e.date] = g[e.date] || []).push(e); }
    return g;
  }, [events]);

  const monthLabel = weekStart.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const today = new Date();
  const todayLabel = today.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
  const weekNum = isoWeek(weekStart);

  // Swipe/scroll between weeks — supports mouse drag (desktop), touch
  // (mobile) and horizontal trackpad wheel.
  const swipeRef = useRef({ x: 0, dragging: false });
  const nextWeek = useCallback(() => setWeekStart((w) => addDays(w, 7)), []);
  const prevWeek = useCallback(() => setWeekStart((w) => addDays(w, -7)), []);
  const onWheel = useCallback((e) => {
    const dx = e.deltaX;
    if (Math.abs(dx) < 40) return;
    // Debounce so one trackpad fling flips exactly one week.
    if (swipeRef.current._wheelLock) return;
    swipeRef.current._wheelLock = true;
    setTimeout(() => { swipeRef.current._wheelLock = false; }, 350);
    if (dx > 0) nextWeek(); else prevWeek();
  }, [nextWeek, prevWeek]);
  const onPointerDown = (e) => { swipeRef.current = { x: e.clientX, dragging: true }; };
  const onPointerUp = (e) => {
    if (!swipeRef.current.dragging) return;
    const dx = e.clientX - swipeRef.current.x;
    swipeRef.current.dragging = false;
    if (Math.abs(dx) > 60) { if (dx < 0) nextWeek(); else prevWeek(); }
  };
  const onTouchStart = (e) => { swipeRef.current = { x: e.touches[0].clientX, dragging: true }; };
  const onTouchEnd = (e) => {
    if (!swipeRef.current.dragging) return;
    const dx = (e.changedTouches[0].clientX || 0) - swipeRef.current.x;
    swipeRef.current.dragging = false;
    if (Math.abs(dx) > 50) { if (dx < 0) nextWeek(); else prevWeek(); }
  };

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 ${compact ? "p-2" : "p-3"} shadow-sm`} data-testid="week-calendar"
      onWheel={onWheel}
      onPointerDown={onPointerDown} onPointerUp={onPointerUp}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Big top header — today + ISO week number */}
      <div className="flex items-center justify-between mb-3 px-1 gap-3 flex-wrap">
        <div>
          <div className="text-[22px] font-black text-slate-900 capitalize leading-tight" data-testid="cal-today-label">{todayLabel}</div>
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 capitalize">{monthLabel} · {t("cal.week").toLowerCase()} {weekNum}</div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, -7))} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="cal-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" onClick={() => setWeekStart(startOfWeek(new Date()))} className="px-3 py-1.5 rounded-lg hover:bg-slate-100 text-[11px] font-black uppercase tracking-wider text-slate-600" data-testid="cal-this-week">{t("cal.today")}</button>
          <button type="button" onClick={() => setShowJump(true)} className="w-8 h-8 rounded-lg hover:bg-cyan-50 hover:text-cyan-700 flex items-center justify-center text-slate-600" data-testid="cal-jump-btn" title={t("cal.jump_title")}><CalendarDays className="w-4 h-4" /></button>
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="cal-next-week"><ChevronRight className="w-4 h-4" /></button>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 ml-1" />}
        </div>
      </div>
      {showJump && (
        <JumpPicker
          initial={weekStart}
          locale={locale}
          t={t}
          onPick={(d) => { setWeekStart(startOfWeek(d)); setShowJump(false); }}
          onClose={() => setShowJump(false)}
        />
      )}

      {/* 7-day strip */}
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d, i) => {
          const key = iso(d);
          const dayEvents = grouped[key] || [];
          const isToday = isSameDay(d, today);
          return (
            <button
              key={key}
              type="button"
              onClick={() => setDayCtx({ date: key, dayName: DAYS_LONG[i], kindMeta: KIND_META, locale, t })}
              data-testid={`cal-day-${key}`}
              className={`relative text-left rounded-xl border transition p-2 min-h-[60px] ${isToday ? "border-cyan-400 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"}`}
            >
              <div className="flex items-center justify-between">
                <div className={`text-[10px] font-black uppercase tracking-wider ${isToday ? "text-cyan-700" : "text-slate-500"}`}>{DAYS_SHORT[i]}</div>
                <div className={`text-sm font-black ${isToday ? "text-cyan-700" : "text-slate-700"}`}>{d.getDate()}</div>
              </div>
              {dayEvents.length > 0 && (
                <div className="mt-1 flex items-center gap-0.5">
                  {dayEvents.slice(0, 5).map((ev) => (
                    <span key={ev.id} className={`w-1.5 h-1.5 rounded-full ${COLOR_DOT[ev.color] || COLOR_DOT.cyan}`} />
                  ))}
                  {dayEvents.length > 5 && <span className="text-[9px] font-bold text-slate-400 ml-0.5">+{dayEvents.length - 5}</span>}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Horizontal note cards row — day label chip + its events */}
      <div className="mt-3 pt-3 border-t border-slate-100 overflow-x-auto">
        <div className="flex items-start gap-2 min-w-max pb-1" data-testid="cal-notes-row">
          {days.map((d, i) => {
            const key = iso(d);
            const dayEvents = grouped[key] || [];
            if (dayEvents.length === 0) return null;
            const isToday = isSameDay(d, today);
            return (
              <div key={key} className="flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={() => setDayCtx({ date: key, dayName: DAYS_LONG[i], kindMeta: KIND_META, locale, t })}
                  className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-black whitespace-nowrap capitalize ${isToday ? "bg-cyan-600 text-white" : "bg-slate-900 text-white"}`}>
                  {DAYS_LONG[i]} {d.getDate()}
                </button>
                {dayEvents.map((ev) => {
                  const meta = KIND_META[ev.kind] || KIND_META.note;
                  const Icon = meta.icon;
                  return (
                    <button key={ev.id} type="button" onClick={() => setDayCtx({ date: key, dayName: DAYS_LONG[i], kindMeta: KIND_META, locale, t })}
                      data-testid={`cal-note-card-${ev.id}`}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-bold text-slate-700 max-w-[200px]`}>
                      <span className={`w-2 h-2 rounded-full ${COLOR_DOT[ev.color] || COLOR_DOT.cyan} flex-shrink-0`} />
                      <Icon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      {ev.start_time && <span className="text-slate-400 text-[10px] font-black">{ev.start_time}</span>}
                      <span className="truncate">{ev.title}</span>
                      {ev.shared_with && ev.shared_with.length > 0 && <UsersIcon className="w-3 h-3 text-violet-500 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {events.length === 0 && (
            <div className="text-xs text-slate-400 italic px-2 py-1.5">{t("cal.empty_week")}</div>
          )}
        </div>
      </div>

      {/* Today's agenda — quick peek at what's next */}
      <TodayAgenda
        events={events}
        locale={locale}
        t={t}
        kindMeta={KIND_META}
        dayLongLabels={DAYS_LONG}
        onOpen={(dateIso, dayIdx) => setDayCtx({ date: dateIso, dayName: DAYS_LONG[dayIdx], kindMeta: KIND_META, locale, t })}
      />
      {dayCtx && (
        <DayModal
          ctx={dayCtx}
          events={grouped[dayCtx.date] || []}
          authHeader={authHeader}
          onClose={() => setDayCtx(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function DayModal({ ctx, events, authHeader, onClose, onChanged }) {
  const t = ctx.t;
  const KIND_META = ctx.kindMeta;
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // event id being edited
  return (
    <div className="fixed inset-0 z-[90] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="cal-day-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mt-10">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">Calendar</div>
            <h2 className="text-lg font-black text-slate-900 capitalize">{ctx.dayName} · {ctx.date}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
          {events.length === 0 && !adding && (
            <div className="text-center py-6 text-slate-400">
              <CalendarCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <div className="text-sm">{t("cal.no_events")}</div>
            </div>
          )}
          {events.map((ev) => (
            editing === ev.id
              ? <EventForm key={ev.id} authHeader={authHeader} initial={ev} kindMeta={KIND_META} t={t} onSaved={() => { setEditing(null); onChanged(); }} onCancel={() => setEditing(null)} />
              : <EventCard key={ev.id} ev={ev} authHeader={authHeader} kindMeta={KIND_META} t={t} onEdit={() => setEditing(ev.id)} onChanged={onChanged} />
          ))}
          {adding && (
            <EventForm authHeader={authHeader} kindMeta={KIND_META} t={t}
              initial={{ date: ctx.date, kind: "note", color: "cyan", shared_with: [], title: "", notes: "", start_time: "", end_time: "", remind_minutes: 0 }}
              onSaved={() => { setAdding(false); onChanged(); }} onCancel={() => setAdding(false)} />
          )}
          {!adding && (
            <button type="button" onClick={() => setAdding(true)} data-testid="cal-add-event"
              className="w-full py-2.5 rounded-xl border-2 border-dashed border-slate-300 hover:border-cyan-400 hover:bg-cyan-50 text-slate-500 hover:text-cyan-700 text-sm font-bold inline-flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4" /> {t("cal.add_event")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EventCard({ ev, authHeader, kindMeta, t, onEdit, onChanged }) {
  const KIND_META = kindMeta;
  const Icon = (KIND_META[ev.kind] || KIND_META.note).icon;
  const meta = KIND_META[ev.kind] || KIND_META.note;
  const del = async () => {
    if (!window.confirm(t("cal.delete_q"))) return;
    try {
      await axios.delete(`${API}/doserne/calendar/event/${ev.id}`, authHeader());
      toast.success(t("cal.deleted"));
      onChanged();
    } catch { toast.error(t("cal.delete_q")); }
  };
  return (
    <div className="border border-slate-200 rounded-xl p-3" data-testid={`cal-event-${ev.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          <span className={`shrink-0 w-8 h-8 rounded-lg ${meta.cls} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-black text-slate-900 text-sm truncate">{ev.title}</div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5 flex-wrap">
              {ev.start_time && (<span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{ev.start_time}{ev.end_time ? ` – ${ev.end_time}` : ""}</span>)}
              <span className="font-bold">{meta.label}</span>
              {ev.remind_minutes > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><Bell className="w-3 h-3" />{reminderLabel(ev.remind_minutes, t)}</span>
              )}
            </div>
            {ev.notes && <div className="text-[12px] text-slate-600 mt-1 whitespace-pre-wrap">{ev.notes}</div>}
            {ev.shared_with && ev.shared_with.length > 0 && (
              <div className="mt-1.5 flex items-center gap-1 text-[10px] text-violet-700">
                <UsersIcon className="w-3 h-3" /> {ev.shared_with.join(", ")}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button type="button" onClick={onEdit} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-bold" title={t("cal.save")}>✎</button>
          <button type="button" onClick={del} className="w-7 h-7 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" title={t("cal.deleted")}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

function reminderLabel(mins, t) {
  if (mins === 0) return t("cal.remind_at_start");
  if (mins === 5) return t("cal.remind_5m");
  if (mins === 15) return t("cal.remind_15m");
  if (mins === 30) return t("cal.remind_30m");
  if (mins === 60) return t("cal.remind_1h");
  if (mins === 1440) return t("cal.remind_1d");
  return `${mins} min`;
}

function EventForm({ authHeader, initial, kindMeta, t, onSaved, onCancel }) {
  const KIND_META = kindMeta;
  const [v, setV] = useState({
    remind_minutes: 0,
    ...initial,
    shared_with: initial.shared_with || [],
  });
  const [saving, setSaving] = useState(false);
  // Colleague picker — fetched when the user picks the "share" kind.
  const [colleagues, setColleagues] = useState([]);
  const [colleaguesLoading, setColleaguesLoading] = useState(false);
  useEffect(() => {
    if (v.kind !== "share" || colleagues.length > 0) return;
    setColleaguesLoading(true);
    axios.get(`${API}/doserne/colleagues`, authHeader())
      .then(({ data }) => {
        // Only ADMINS with real emails; dedup by lowercase email.
        const seen = new Set();
        const out = [];
        for (const c of (data?.colleagues || [])) {
          if (!c.is_admin) continue;
          const em = (c.email || "").trim().toLowerCase();
          if (!em || seen.has(em)) continue;
          seen.add(em);
          out.push({ email: em, name: c.name || c.email });
        }
        out.sort((a, b) => a.name.localeCompare(b.name));
        setColleagues(out);
      })
      .catch(() => setColleagues([]))
      .finally(() => setColleaguesLoading(false));
  }, [v.kind, colleagues.length, authHeader]);
  const toggleShared = (email) => {
    const em = email.toLowerCase();
    setV((prev) => {
      const list = prev.shared_with || [];
      const has = list.map((x) => x.toLowerCase()).includes(em);
      return { ...prev, shared_with: has ? list.filter((x) => x.toLowerCase() !== em) : [...list, em] };
    });
  };
  const save = async () => {
    if (!v.title.trim()) { toast.error(t("cal.title_required")); return; }
    setSaving(true);
    try {
      const payload = {
        title: v.title.trim(),
        notes: v.notes || "",
        date: v.date,
        start_time: v.start_time || "",
        end_time: v.end_time || "",
        kind: v.kind,
        color: v.color || "cyan",
        shared_with: v.kind === "share" ? (v.shared_with || []) : [],
        remind_minutes: Number(v.remind_minutes || 0),
      };
      if (initial.id) {
        await axios.patch(`${API}/doserne/calendar/event/${initial.id}`, payload, authHeader());
      } else {
        await axios.post(`${API}/doserne/calendar/event`, payload, authHeader());
      }
      toast.success(t("cal.saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };
  return (
    <div className="border-2 border-cyan-200 rounded-xl p-3 bg-cyan-50/40 space-y-2.5" data-testid="cal-event-form">
      <input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder={t("cal.title_ph")}
        data-testid="cal-form-title" autoFocus
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-bold focus:outline-none focus:border-cyan-400" />
      <textarea value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} placeholder={t("cal.notes_ph")} rows={2}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-cyan-400 resize-none" />

      <div className="grid grid-cols-3 gap-2">
        <div>
          <div className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-0.5">{t("cal.date")}</div>
          <input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })}
            title={t("cal.date_tt")}
            className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-cyan-400" />
        </div>
        <div>
          <div className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-0.5">{t("cal.start_time")}</div>
          <input type="time" value={v.start_time} onChange={(e) => setV({ ...v, start_time: e.target.value })}
            title={t("cal.start_tt")}
            className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-cyan-400" />
        </div>
        <div>
          <div className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-0.5">{t("cal.end_time")}</div>
          <input type="time" value={v.end_time} onChange={(e) => setV({ ...v, end_time: e.target.value })}
            title={t("cal.end_tt")}
            className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-cyan-400" />
        </div>
      </div>
      <div className="text-[10px] text-slate-500 -mt-1 italic">{t("cal.allday_hint")}</div>

      <div>
        <div className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Bell className="w-3 h-3" /> {t("cal.remind")}</div>
        <select value={v.remind_minutes || 0} onChange={(e) => setV({ ...v, remind_minutes: Number(e.target.value) })}
          disabled={!v.start_time}
          title={v.start_time ? t("cal.remind_tt") : t("cal.start_tt")}
          data-testid="cal-form-remind"
          className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-cyan-400 bg-white disabled:bg-slate-100 disabled:text-slate-400">
          <option value={0}>{t("cal.remind_off")}</option>
          <option value={5}>{t("cal.remind_5m")}</option>
          <option value={15}>{t("cal.remind_15m")}</option>
          <option value={30}>{t("cal.remind_30m")}</option>
          <option value={60}>{t("cal.remind_1h")}</option>
          <option value={1440}>{t("cal.remind_1d")}</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {Object.entries(KIND_META).map(([k, m]) => {
          const KindIcon = m.icon;
          return (
            <button key={k} type="button" onClick={() => setV({ ...v, kind: k })} data-testid={`cal-kind-${k}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-black ${v.kind === k ? m.cls : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              <KindIcon className="w-3 h-3" /> {m.label}
            </button>
          );
        })}
      </div>

      {v.kind === "share" && (
        <div className="border border-violet-200 bg-violet-50/50 rounded-lg p-2">
          <div className="text-[10px] font-black uppercase tracking-wider text-violet-700 mb-1.5 flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {t("cal.share_header")}</div>
          {colleaguesLoading ? (
            <div className="text-[11px] text-slate-400 py-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t("cal.share_loading")}</div>
          ) : colleagues.length === 0 ? (
            <div className="text-[11px] text-slate-500 italic">{t("cal.share_empty")}</div>
          ) : (
            <div className="flex flex-wrap gap-1" data-testid="cal-shared-picker">
              {colleagues.map((c) => {
                const picked = (v.shared_with || []).map((x) => x.toLowerCase()).includes(c.email);
                return (
                  <button key={c.email} type="button" onClick={() => toggleShared(c.email)} data-testid={`cal-shared-${c.email}`}
                    title={c.email}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black border transition ${picked ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"}`}>
                    {picked && <Check className="w-2.5 h-2.5" />}
                    <span className="truncate max-w-[110px]">{c.name}</span>
                  </button>
                );
              })}
            </div>
          )}
          {(v.shared_with || []).length > 0 && (
            <div className="text-[10px] text-violet-700 mt-1.5 font-bold">
              {(v.shared_with || []).length === 1 ? t("cal.share_summary_one") : t("cal.share_summary_many").replace("{{n}}", (v.shared_with || []).length)}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-1">{t("cal.color")}</div>
        <div className="flex items-center gap-1.5">
          {Object.keys(COLOR_DOT).map((c) => (
            <button key={c} type="button" onClick={() => setV({ ...v, color: c })} title={c}
              className={`w-5 h-5 rounded-full ${COLOR_DOT[c]} ${v.color === c ? "ring-2 ring-offset-1 ring-slate-800" : ""}`} />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100 text-xs font-bold">{t("cal.cancel")}</button>
        <button type="button" onClick={save} disabled={saving} data-testid="cal-form-save"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-black disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {t("cal.save")}
        </button>
      </div>
    </div>
  );
}


function JumpPicker({ initial, locale, t, onPick, onClose }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(initial); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const monthLabel = cursor.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const firstDow = (new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const dayShort = useMemo(() => {
    const base = new Date(2024, 0, 1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base); d.setDate(base.getDate() + i);
      return d.toLocaleDateString(locale, { weekday: "narrow" });
    });
  }, [locale]);
  const today = new Date();
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const sameInitial = (d) => sameDay(d, initial);
  const prev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const next = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  // Flat cell list — blanks + days
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4" onClick={onClose} data-testid="cal-jump-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs mt-10 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <button type="button" onClick={prev} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="cal-jump-prev"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-[13px] font-black text-slate-900 capitalize">{monthLabel}</div>
          <button type="button" onClick={next} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="cal-jump-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="px-3 pt-2 pb-3">
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {dayShort.map((lbl, i) => (
              <div key={i} className="text-center text-[9px] font-black uppercase tracking-wider text-slate-400 py-1">{lbl}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const isToday = sameDay(d, today);
              const isSelected = sameInitial(d);
              return (
                <button key={i} type="button" onClick={() => onPick(d)} data-testid={`cal-jump-day-${d.getDate()}`}
                  className={`h-8 rounded-lg text-[12px] font-black transition ${
                    isToday ? "bg-cyan-600 text-white hover:bg-cyan-500" :
                    isSelected ? "bg-slate-900 text-white hover:bg-slate-800" :
                    "text-slate-700 hover:bg-cyan-50 hover:text-cyan-700"
                  }`}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
            <button type="button" onClick={() => onPick(new Date())} className="px-2.5 py-1.5 rounded-lg bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-[11px] font-black" data-testid="cal-jump-today">
              {t("cal.jump_today")}
            </button>
            <button type="button" onClick={onClose} className="px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-slate-500 text-[11px] font-black" data-testid="cal-jump-close">
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function TodayAgenda({ events, locale, t, kindMeta, dayLongLabels, onOpen }) {
  const now = new Date();
  const todayIso = iso(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = (hhmm) => {
    const [h, m] = (hhmm || "").split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(h)) return -1;
    return h * 60 + (m || 0);
  };
  const dayIdxOf = (dateStr) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      return (d.getDay() + 6) % 7;
    } catch { return 0; }
  };
  const todayAll = events.filter((e) => e.date === todayIso).sort((a, b) => toMin(a.start_time) - toMin(b.start_time));
  const upcomingToday = todayAll.filter((e) => toMin(e.start_time) === -1 || toMin(e.start_time) >= nowMin);
  // Fallback: next upcoming event this week (any day after today, or today past time).
  const futureThisWeek = events
    .filter((e) => e.date > todayIso || (e.date === todayIso && toMin(e.start_time) >= nowMin))
    .sort((a, b) => (a.date === b.date ? toMin(a.start_time) - toMin(b.start_time) : a.date.localeCompare(b.date)));
  const nextEvent = futureThisWeek[0];
  // Nothing to show at all? Hide the panel entirely to avoid noise.
  if (upcomingToday.length === 0 && !nextEvent) return null;

  const showingToday = upcomingToday.length > 0;
  const list = showingToday ? upcomingToday.slice(0, 4) : [nextEvent];

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="cal-today-agenda">
      <div className="flex items-center gap-1.5 mb-2 px-1">
        <Zap className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {showingToday ? t("cal.agenda_today") : t("cal.agenda_next")}
        </span>
        {showingToday && todayAll.length > 4 && (
          <span className="text-[10px] font-black text-slate-400">+{todayAll.length - 4}</span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        {list.map((ev) => {
          const meta = kindMeta[ev.kind] || kindMeta.note;
          const Icon = meta.icon;
          const dayIdx = dayIdxOf(ev.date);
          const isToday = ev.date === todayIso;
          let dayPrefix = "";
          if (!isToday) {
            try {
              const d = new Date(ev.date + "T00:00:00");
              dayPrefix = d.toLocaleDateString(locale, { weekday: "short", day: "numeric" });
            } catch { dayPrefix = ev.date; }
          }
          return (
            <button key={ev.id} type="button" onClick={() => onOpen(ev.date, dayIdx)}
              data-testid={`cal-agenda-${ev.id}`}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-xl border border-slate-200 bg-gradient-to-r from-amber-50/40 via-white to-white hover:border-amber-300 hover:bg-amber-50/60 transition text-left">
              <span className={`shrink-0 w-7 h-7 rounded-lg ${meta.cls} flex items-center justify-center`}>
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                {!isToday && (
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 capitalize">{dayPrefix}</span>
                )}
                {ev.start_time ? (
                  <span className="text-[12px] font-black text-slate-900 tabular-nums">
                    {ev.start_time}{ev.end_time ? `–${ev.end_time}` : ""}
                  </span>
                ) : (
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t("cal.allday_short")}</span>
                )}
                <span className="text-[12px] font-bold text-slate-700 truncate flex-1 min-w-0">{ev.title}</span>
                {ev.shared_with && ev.shared_with.length > 0 && (
                  <UsersIcon className="w-3 h-3 text-violet-500 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
